import React from 'react';
import { getAvailableDetailItems } from '../../utils/guided/templateBuilder';
import './GuidedEmailTemplateForm.css';

const appendToken = (value, field) => {
  const token = `{{${field}}}`;
  if (!value) {
    return token;
  }

  return value.includes(token) ? value : `${value.trimEnd()} ${token}`;
};

const GuidedField = ({
  label,
  value,
  helper,
  multiline = false,
  mergeFields,
  onChange
}) => {
  const InputTag = multiline ? 'textarea' : 'input';

  return (
    <label className="guided-template-form__field">
      <span className="guided-template-form__label">{label}</span>
      <InputTag
        className={`guided-template-form__input${multiline ? ' guided-template-form__input--textarea' : ''}`}
        rows={multiline ? 4 : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <div className="guided-template-form__token-row">
        {mergeFields.map((field) => (
          <button
            key={`${label}-${field}`}
            type="button"
            className="guided-template-form__token"
            onClick={() => onChange(appendToken(value, field))}
          >
            {`{{${field}}}`}
          </button>
        ))}
      </div>
      {helper ? <span className="guided-template-form__helper">{helper}</span> : null}
    </label>
  );
};

const GuidedEmailTemplateForm = ({ value, mergeFields, visibleMergeFields = mergeFields, onChange }) => {
  const detailItems = getAvailableDetailItems(mergeFields);
  const orderedDetailItems = [
    ...(Array.isArray(value.detailsOrder) ? value.detailsOrder : []),
    ...detailItems.map((item) => item.id)
  ]
    .filter((itemId, index, list) => list.indexOf(itemId) === index)
    .map((itemId) => detailItems.find((item) => item.id === itemId))
    .filter(Boolean);

  const updateField = (field, nextValue) => {
    onChange({
      ...value,
      [field]: nextValue
    });
  };

  const moveDetailItem = (index, direction) => {
    const currentOrder = orderedDetailItems.map((item) => item.id);
    const nextIndex = index + direction;

    if (nextIndex < 0 || nextIndex >= currentOrder.length) {
      return;
    }

    [currentOrder[index], currentOrder[nextIndex]] = [currentOrder[nextIndex], currentOrder[index]];
    updateField('detailsOrder', currentOrder);
  };

  return (
    <div className="guided-template-form">
      <GuidedField
        label="Greeting"
        value={value.greeting}
        mergeFields={visibleMergeFields}
        helper="Start with a friendly opening like Hi {{customerName}},"
        onChange={(nextValue) => updateField('greeting', nextValue)}
      />

      <GuidedField
        label="Intro message"
        value={value.intro}
        multiline
        mergeFields={visibleMergeFields}
        helper="Use plain language to explain why the customer is getting this email."
        onChange={(nextValue) => updateField('intro', nextValue)}
      />

      <label className="guided-template-form__toggle">
        <input
          type="checkbox"
          checked={value.includeDetailsCard}
          onChange={(event) => updateField('includeDetailsCard', event.target.checked)}
        />
        <span>Show the appointment details card</span>
      </label>

      {value.includeDetailsCard ? (
        <>
          <GuidedField
            label="Details card heading"
            value={value.detailsHeading}
            mergeFields={[]}
            helper="Examples: Appointment details, Updated appointment details, Payment details"
            onChange={(nextValue) => updateField('detailsHeading', nextValue)}
          />

          {detailItems.length > 1 ? (
            <div className="guided-template-form__details-order">
              <div className="guided-template-form__details-order-copy">
                <span className="guided-template-form__label">Details card order</span>
                <span className="guided-template-form__helper">Move the rows to choose which appointment details show first.</span>
              </div>
              <div className="guided-template-form__details-order-list">
                {orderedDetailItems.map((item, index) => (
                    <div key={item.id} className="guided-template-form__details-order-item">
                      <span className="guided-template-form__details-order-name">{item.label}</span>
                      <div className="guided-template-form__details-order-actions">
                        <button
                          type="button"
                          className="guided-template-form__details-order-button"
                          onClick={() => moveDetailItem(index, -1)}
                          disabled={index === 0}
                          aria-label={`Move ${item.label} up`}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="guided-template-form__details-order-button"
                          onClick={() => moveDetailItem(index, 1)}
                          disabled={index === orderedDetailItems.length - 1}
                          aria-label={`Move ${item.label} down`}
                        >
                          Down
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <GuidedField
        label="Closing message"
        value={value.closing}
        multiline
        mergeFields={visibleMergeFields}
        helper="Tell them what to do next or what to expect."
        onChange={(nextValue) => updateField('closing', nextValue)}
      />

      <GuidedField
        label="Signature"
        value={value.signature}
        multiline
        mergeFields={visibleMergeFields}
        helper="Short sign-off text shown at the end of the email."
        onChange={(nextValue) => updateField('signature', nextValue)}
      />

      {mergeFields.includes('portalUrl') ? (
        <GuidedField
          label="Button text"
          value={value.ctaText}
          mergeFields={[]}
          helper="Optional. Leave blank if you do not want a booking button."
          onChange={(nextValue) => updateField('ctaText', nextValue)}
        />
      ) : null}
    </div>
  );
};

export default GuidedEmailTemplateForm;
